import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Link as LinkIcon, Copy, Check, Mail, ExternalLink, Edit2 } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';
const BASE_URL = 'https://tradebot-k4nt.onrender.com';

export default function PaymentLink() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [amount, setAmount] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  // Email confirmation state — mirrors setConfirmEmaiDisplay / handelSubmitConfirmEmail
  const [email, setEmail] = useState('');
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [confirmingEmail, setConfirmingEmail] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(true);

  useEffect(() => {
    if (!user?.telegramName) return;
    // Mirrors setConfirmEmaiDisplay in index.js: check if email exists
    if (user.email) {
      setEmail(user.email);
      setEmailConfirmed(true);
      setShowEmailForm(false);
    } else {
      setShowEmailForm(true);
      setEmailConfirmed(false);
    }
    setLoadingEmail(false);
  }, [user]);

  // Mirrors handelSubmitConfirmEmail in index.js
  const handleConfirmEmail = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setConfirmingEmail(true);
    try {
      const response = await fetch(`${API_BASE}/payment/confirm-email`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user.telegramName, email })
      });
      const data = await response.json();
      if (response.status === 200) {
        toast.success('Email confirmed!');
        setEmailConfirmed(true);
        setShowEmailForm(false);
      } else {
        toast.error(data?.error || 'Invalid email');
      }
    } catch {
      toast.error('Network error. Try again.');
    } finally {
      setConfirmingEmail(false);
    }
  };

  // Mirrors handelSubmitPaymentLink in index.js
  const handleGenerate = (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    const link = `${BASE_URL}/?username=${user.telegramName}&deposit=${amount}`;
    setGeneratedLink(link);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      toast.success('Payment link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const quickAmounts = [10, 25, 50, 100, 250, 500];

  if (loadingEmail) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-700 hover:text-slate-900 mb-6 transition"
        >
          <ArrowLeft size={20} /> Back to Dashboard
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <LinkIcon className="text-blue-600" size={28} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Payment Link</h1>
          <p className="text-slate-600 mt-2">Generate a shareable payment link for @{user?.telegramName}</p>
        </div>

        {/* STEP 1: Email confirmation — mirrors payement-link.html / confarm-email flow */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${emailConfirmed ? 'bg-green-100' : 'bg-blue-100'}`}>
                <Mail className={emailConfirmed ? 'text-green-600' : 'text-blue-600'} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Email Address</h3>
                <p className="text-sm text-slate-500">
                  {emailConfirmed ? email : 'Required to receive payments'}
                </p>
              </div>
            </div>
            {emailConfirmed && (
              <button
                onClick={() => setShowEmailForm(true)}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
              >
                <Edit2 size={14} /> Edit
              </button>
            )}
          </div>

          {showEmailForm && (
            <form onSubmit={handleConfirmEmail} className="space-y-3">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                required
              />
              <div className="flex gap-3">
                {emailConfirmed && (
                  <button
                    type="button"
                    onClick={() => setShowEmailForm(false)}
                    className="flex-1 py-2.5 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition font-medium"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={confirmingEmail}
                  className="flex-1 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {confirmingEmail ? 'Confirming...' : 'Confirm Email'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* STEP 2: Generate link — only shown when email confirmed */}
        {emailConfirmed && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            {!generatedLink ? (
              <>
                <h3 className="font-semibold text-slate-900 mb-4">Set Payment Amount</h3>

                {/* Quick amounts — mirrors deposit page pattern */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {quickAmounts.map((val) => (
                    <button
                      key={val}
                      onClick={() => setAmount(val.toString())}
                      className={`p-3 border-2 rounded-xl font-semibold transition-all text-sm ${
                        amount === val.toString()
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      ${val}
                    </button>
                  ))}
                </div>

                <form onSubmit={handleGenerate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Custom Amount (USD)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-lg font-medium">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-8 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-lg transition"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition shadow-sm flex items-center justify-center gap-2"
                  >
                    <LinkIcon size={18} />
                    Generate Payment Link
                  </button>
                </form>
              </>
            ) : (
              <div className="space-y-6">
                <h3 className="font-semibold text-slate-900">Your Payment Link</h3>

                {/* QR Code */}
                <div className="flex justify-center p-6 bg-slate-50 rounded-2xl border border-slate-200">
                  <QRCode value={generatedLink} size={200} level="H" includeMargin />
                </div>

                {/* Link display + copy */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide font-medium">Payment Link</p>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-mono text-slate-700 overflow-hidden text-ellipsis whitespace-nowrap">
                      {generatedLink}
                    </div>
                    <button
                      onClick={handleCopy}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 shrink-0 shadow-sm"
                    >
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {/* Open link */}
                <a
                  href={generatedLink}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-3 border-2 border-blue-200 text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition flex items-center justify-center gap-2"
                >
                  <ExternalLink size={16} />
                  Open Payment Page
                </a>

                <button
                  onClick={() => { setGeneratedLink(null); setAmount(''); }}
                  className="w-full py-3 border border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition"
                >
                  Generate New Link
                </button>
              </div>
            )}
          </div>
        )}

        {!emailConfirmed && (
          <div className="text-center py-6 text-slate-400 text-sm">
            Confirm your email above to generate a payment link
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
